import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

/**
 * ScrollToTop component ensures that every navigation jump starts at the top of the page.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/**
 * SubscriptionGuard component checks if user has remaining credits before allowing access.
 * If no credits, shows upgrade modal and redirects to dashboard.
 */
function SubscriptionGuard({ children, user, onShowUpgradeModal }) {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [hasCredits, setHasCredits] = useState(true);
  const hasChecked = useRef(false);

  useEffect(() => {
    // Only check once per mount
    if (hasChecked.current) return;
    hasChecked.current = true;

    const checkSubscription = async () => {
      if (!user?.id) {
        setIsChecking(false);
        return;
      }

      try {
        const apiBase = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || '');
        const res = await fetch(`${apiBase}/api/user/subscription-status`, {
          headers: { 'x-clerk-user-id': user.id }
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.creditsRemaining === 0) {
            setHasCredits(false);
            onShowUpgradeModal();
            navigate('/dashboard', { replace: true });
            return;
          }
        }
      } catch (err) {
        console.warn('[SubscriptionGuard] Failed to check subscription:', err);
      }
      
      setIsChecking(false);
    };

    checkSubscription();
  }, [user, navigate, onShowUpgradeModal]);

  // Show nothing while checking (brief loading state)
  if (isChecking && user?.id) {
    return null;
  }

  // If no credits, don't render children (we're redirecting)
  if (!hasCredits) {
    return null;
  }

  return children;
}

import { useUser } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { identifyUser, resetUser, captureEvent, trackPageView, updateUserProperties } from './lib/posthog';
import OnboardingPage from './pages/OnboardingPage';
import AnalysisPage from './pages/AnalysisPage';
import Sidebar from './components/layout/Sidebar';
import { SidebarProvider } from './components/layout/SidebarContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Hero from './components/hero/Hero';
import UpgradeModal from './components/UpgradeModal';
import VideoTooShortModal from './components/VideoTooShortModal';
import VideoTooLargeModal from './components/VideoTooLargeModal';
import VideoDuplicateModal from './components/VideoDuplicateModal';
import ErrorDisplay from './components/ErrorDisplay';
import FeaturesSection from './components/homepage/FeaturesSection';
import HowItWorksSection from './components/homepage/HowItWorksSection';
import SocialProofSection from './components/homepage/SocialProofSection';
import CTASection from './components/homepage/CTASection';
import { SignedOut } from '@clerk/clerk-react';
import TeamsContactPage from './pages/TeamsContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import RefundsPage from './pages/RefundsPage';
import ContactPage from './pages/ContactPage';
import LoadingSpinner from './components/LoadingSpinner';
import SEO, { generateSoftwareApplicationSchema, SEOConfigs } from './components/SEO';
import { sanitizeHeaderValue } from './utils/httpHeaders';
import { startVersionCheck } from './utils/versionCheck';
import { uploadVideoToS3 } from './utils/s3Upload';

// Lazy load dashboard pages for code splitting and faster initial load
// Add error handling for failed dynamic imports (e.g., cache issues, dev server not running)
const lazyWithErrorHandling = (importFn, componentName) => {
  return lazy(() => {
    return importFn().catch(err => {
      console.error(`[AppRouter] Failed to load ${componentName}:`, err);
      // In development, show helpful error
      if (import.meta.env.DEV) {
        const errorMessage = `Failed to load ${componentName}. This usually means:\n1. Vite dev server is not running (run: npm run client)\n2. Browser cache needs clearing (Ctrl+Shift+R)\n3. Service worker needs unregistering (DevTools > Application > Service Workers)`;
        alert(errorMessage);
      }
      // Return a fallback component that shows an error
      return {
        default: () => (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h2>Failed to load {componentName}</h2>
            <p>Please refresh the page (Ctrl+Shift+R) or clear your browser cache.</p>
            <button onClick={() => window.location.reload()}>Reload Page</button>
          </div>
        )
      };
    });
  });
};

const Dashboard = lazyWithErrorHandling(() => import('./components/Dashboard'), 'Dashboard');
const MyAnalysesPage = lazyWithErrorHandling(() => import('./pages/MyAnalysesPage'), 'MyAnalysesPage');
const MyProgressPage = lazyWithErrorHandling(() => import('./pages/MyProgressPage'), 'MyProgressPage');
const PracticePage = lazyWithErrorHandling(() => import('./pages/PracticePage'), 'PracticePage');
const PricingPage = lazyWithErrorHandling(() => import('./pages/PricingPage'), 'PricingPage');
const SubscriptionPage = lazyWithErrorHandling(() => import('./pages/SubscriptionPage'), 'SubscriptionPage');
const SubscriptionSuccessPage = lazyWithErrorHandling(() => import('./pages/SubscriptionSuccessPage'), 'SubscriptionSuccessPage');
const SubscriptionCancelPage = lazyWithErrorHandling(() => import('./pages/SubscriptionCancelPage'), 'SubscriptionCancelPage');
const CoursesPage = lazyWithErrorHandling(() => import('./pages/CoursesPage'), 'CoursesPage');
const SettingsPage = lazyWithErrorHandling(() => import('./pages/SettingsPage'), 'SettingsPage');
const SignInPage = lazyWithErrorHandling(() => import('./pages/SignInPage'), 'SignInPage');
const SignUpPage = lazyWithErrorHandling(() => import('./pages/SignUpPage'), 'SignUpPage');

// Protected Route Component
function ProtectedRoute({ children, requireOnboarding = false }) {
  const { user, isLoaded: userLoaded } = useUser();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  // Initialize from localStorage immediately to prevent initial false negatives
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(() => {
    if (user?.id) {
      return localStorage.getItem(`bodai_questions_completed_${user.id}`) === 'true';
    }
    return false;
  });
  const [userContext, setUserContext] = useState(null);

  const hasCheckedOnboardingRef = useRef(false);
  const lastCheckedUserIdRef = useRef(null);

  // Clear Clerk redirect URLs IMMEDIATELY on mount, before any other logic runs
  // This must run first to prevent Clerk from applying stored redirect URLs
  useEffect(() => {
    // Only clear if we're on a protected route (not sign-in/sign-up)
    const isAuthPage = location.pathname === '/sign-in' ||
      location.pathname === '/sign-up' ||
      location.pathname.startsWith('/sign-in/') ||
      location.pathname.startsWith('/sign-up/');

    if (!isAuthPage) {
      try {
        // Clear ALL Clerk-related redirect URLs from sessionStorage
        const keysToRemove = [];
        Object.keys(sessionStorage).forEach(key => {
          const lowerKey = key.toLowerCase();
          if (lowerKey.includes('clerk') &&
            (lowerKey.includes('redirect') ||
              lowerKey.includes('aftersign') ||
              lowerKey.includes('signin') ||
              lowerKey.includes('signup'))) {
            keysToRemove.push(key);
          }
        });
        keysToRemove.forEach(key => {
          console.log('[ProtectedRoute] Clearing Clerk redirect key:', key);
          sessionStorage.removeItem(key);
        });

        // Also clear from localStorage (Clerk might store there too)
        Object.keys(localStorage).forEach(key => {
          const lowerKey = key.toLowerCase();
          if (lowerKey.includes('clerk') &&
            (lowerKey.includes('redirect') || lowerKey.includes('aftersign'))) {
            console.log('[ProtectedRoute] Clearing Clerk redirect key from localStorage:', key);
            localStorage.removeItem(key);
          }
        });

        // Clear redirect URLs from URL params
        const urlParams = new URLSearchParams(window.location.search);
        let urlChanged = false;
        if (urlParams.has('__clerk_redirect_url')) {
          urlParams.delete('__clerk_redirect_url');
          urlChanged = true;
        }
        if (urlParams.has('redirect_url')) {
          urlParams.delete('redirect_url');
          urlChanged = true;
        }
        if (urlChanged) {
          const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
          window.history.replaceState({}, '', newUrl);
        }
      } catch (e) {
        console.error('[ProtectedRoute] Error clearing redirect URLs:', e);
      }
    }
  }, [location.pathname]); // Run whenever pathname changes

  // Update hasCompletedOnboarding from localStorage when user becomes available or pathname changes
  // This is important after completing onboarding - we need to update state when navigating to dashboard
  useEffect(() => {
    if (user?.id) {
      const saved = localStorage.getItem(`bodai_questions_completed_${user.id}`);
      if (saved === 'true') {
        // Always update state from localStorage if it says completed, regardless of current state
        // This ensures state is synced after completing onboarding
        setHasCompletedOnboarding(true);
        // Force a re-check of onboarding status from API to ensure consistency
        hasCheckedOnboardingRef.current = false;
      }
    }
  }, [user?.id, location.pathname]);

  useEffect(() => {
    if (!userLoaded) return;

    if (!user) {
      hasCheckedOnboardingRef.current = false;
      lastCheckedUserIdRef.current = null;
      // Clear Sentry user context when signed out
      if (import.meta.env.VITE_SENTRY_DSN) {
        Sentry.setUser(null);
      }
      // Reset PostHog user identification when signed out
      resetUser();
      // User not signed in - redirect to home
      if (location.pathname !== '/') {
        navigate('/', { replace: true });
      }
      return;
    }

    // Set Sentry user context when user is available
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.setUser({
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        username: user.username || user.primaryEmailAddress?.emailAddress,
      });
    }

    // Identify user in PostHog
    const email = user.primaryEmailAddress?.emailAddress || (user.emailAddresses && user.emailAddresses[0]?.emailAddress);
    identifyUser(user.id, {
      email: email,
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
    });

    // Check onboarding status
    const checkOnboarding = async () => {
      // Don't re-check if we already checked for this user UNLESS we're on dashboard after onboarding
      // This allows re-checking when user completes onboarding and navigates to dashboard
      const shouldForceRecheck = location.pathname === '/dashboard' &&
        localStorage.getItem(`bodai_questions_completed_${user.id}`) === 'true' &&
        !hasCompletedOnboarding;

      if (hasCheckedOnboardingRef.current && lastCheckedUserIdRef.current === user.id && !shouldForceRecheck) {
        return;
      }

      setCheckingOnboarding(true);
      try {
        lastCheckedUserIdRef.current = user.id;
        // Send user profile data from Clerk to sync with database
        const headers = {
          'X-Clerk-User-Id': user.id,
          'Content-Type': 'application/json'
        };

        // Add user profile data to headers for syncing (sanitize values to ensure valid HTTP header characters)
        const email = user.primaryEmailAddress?.emailAddress || (user.emailAddresses && user.emailAddresses[0]?.emailAddress);
        if (email) {
          headers['X-User-Email'] = sanitizeHeaderValue(email);
        }
        if (user.firstName) {
          headers['X-User-First-Name'] = sanitizeHeaderValue(user.firstName);
        }
        if (user.lastName) {
          headers['X-User-Last-Name'] = sanitizeHeaderValue(user.lastName);
        }
        const phone = user.primaryPhoneNumber?.phoneNumber || (user.phoneNumbers && user.phoneNumbers[0]?.phoneNumber);
        if (phone) {
          headers['X-User-Phone'] = sanitizeHeaderValue(phone);
        }
        if (user.imageUrl) {
          headers['X-User-Image-Url'] = sanitizeHeaderValue(user.imageUrl);
        }

        const apiBase = import.meta.env.DEV
          ? '' // use Vite proxy in dev
          : (import.meta.env.VITE_API_URL || ''); // Use relative URLs in production if VITE_API_URL not set

        const res = await fetch(`${apiBase}/api/user/profile`, {
          headers
        });

        if (res.ok) {
          const data = await res.json();
          const userProfile = data.user;
          const completed = !!userProfile?.onboarding_completed_at;

          // Check if this is a new user (first time seeing profile)
          const isNewUser = !userProfile?.onboarding_completed_at && !lastCheckedUserIdRef.current;

          // Update PostHog user properties with profile data
          identifyUser(user.id, {
            email: user.primaryEmailAddress?.emailAddress || userProfile?.email,
            firstName: user.firstName || userProfile?.first_name,
            lastName: user.lastName || userProfile?.last_name,
            subscription_type: userProfile?.subscription_type || 'free',
            onboarding_completed: completed,
            primary_goal: userProfile?.primary_goal,
            confidence_level: userProfile?.confidence_level,
          });

          // Update user properties (for existing identified users)
          updateUserProperties({
            subscription_type: userProfile?.subscription_type || 'free',
            onboarding_completed: completed,
            primary_goal: userProfile?.primary_goal,
            confidence_level: userProfile?.confidence_level,
            practice_commitment: userProfile?.practice_commitment,
          });

          // Track sign in / sign up events
          if (isNewUser) {
            // First time user - likely just signed up
            captureEvent('user_signed_up', {
              sign_up_method: 'clerk', // Could be enhanced to detect social vs email
            });
          } else if (lastCheckedUserIdRef.current !== user.id) {
            // Different user - sign in
            captureEvent('user_signed_in', {
              has_completed_onboarding: completed,
              subscription_type: userProfile?.subscription_type || 'free',
            });
          }

          // Clear redirect tracking when onboarding status changes to allow legitimate redirects
          const previousCompleted = hasCompletedOnboarding;
          if (previousCompleted !== completed) {
            // Clear all redirect tracking for this user when status changes
            Object.keys(localStorage).forEach(key => {
              if (key.startsWith(`bodai_redirect_${user.id}_`)) {
                localStorage.removeItem(key);
              }
            });
          }

          setHasCompletedOnboarding(completed);
          hasCheckedOnboardingRef.current = true;

          if (completed && userProfile.primary_goal) {
            const context = {
              primaryGoal: userProfile.primary_goal,
              confidenceLevel: userProfile.confidence_level || 'medium',
              includeEnvironmentFeedback: userProfile.include_environment_feedback !== undefined ? userProfile.include_environment_feedback : false,
              practiceCommitment: userProfile.practice_commitment || userProfile.commitment_level || 'regular'
            };
            if (userProfile.goal_specific_context) {
              context.goalSpecificContext = userProfile.goal_specific_context;
            }
            setUserContext(context);
            localStorage.setItem(`bodai_questions_completed_${user.id}`, 'true');
            localStorage.setItem(`bodai_user_context_${user.id}`, JSON.stringify(context));
          } else {
            localStorage.removeItem(`bodai_questions_completed_${user.id}`);
          }
        } else {
          // Fallback to user-specific localStorage
          const saved = localStorage.getItem(`bodai_questions_completed_${user.id}`);
          const savedContext = localStorage.getItem(`bodai_user_context_${user.id}`);
          const savedCompleted = saved === 'true';

          // Clear redirect tracking when onboarding status changes
          const previousCompleted = hasCompletedOnboarding;
          if (previousCompleted !== savedCompleted) {
            Object.keys(localStorage).forEach(key => {
              if (key.startsWith(`bodai_redirect_${user.id}_`)) {
                localStorage.removeItem(key);
              }
            });
          }

          setHasCompletedOnboarding(savedCompleted);
          if (savedContext) {
            setUserContext(JSON.parse(savedContext));
          }
          // Even if it failed, we consider it checked to avoid spamming the API
          hasCheckedOnboardingRef.current = true;
        }
      } catch (error) {
        console.error('Error checking onboarding:', error);
        const saved = localStorage.getItem(`bodai_questions_completed_${user.id}`);
        const savedContext = localStorage.getItem(`bodai_user_context_${user.id}`);
        const savedCompleted = saved === 'true';

        // Clear redirect tracking when onboarding status changes
        const previousCompleted = hasCompletedOnboarding;
        if (previousCompleted !== savedCompleted) {
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith(`bodai_redirect_${user.id}_`)) {
              localStorage.removeItem(key);
            }
          });
        }

        setHasCompletedOnboarding(savedCompleted);
        if (savedContext) {
          setUserContext(JSON.parse(savedContext));
        }
        hasCheckedOnboardingRef.current = true;
      } finally {
        setCheckingOnboarding(false);
      }
    };

    checkOnboarding();
  }, [user, userLoaded, navigate, location.pathname]); // Include location.pathname to re-check when navigating to dashboard after onboarding

  // Handle redirects in useEffect to avoid setState during render
  // This must be before any early returns to satisfy React Hook rules
  useEffect(() => {
    if (!userLoaded || checkingOnboarding || !user) {
      return;
    }

    // Ensure onboarding check has completed at least once before allowing redirects
    if (!hasCheckedOnboardingRef.current) {
      return;
    }

    const isOnOnboardingPage = location.pathname === '/onboarding';
    const currentPath = location.pathname;

    let shouldRedirect = false;
    let targetPath = null;
    let redirectScenario = null;

    if (requireOnboarding && !hasCompletedOnboarding) {
      // Redirect to onboarding if required but not completed
      if (!isOnOnboardingPage) {
        shouldRedirect = true;
        targetPath = '/onboarding';
        redirectScenario = 'to_onboarding';
        console.log('[ProtectedRoute] Will redirect to onboarding - requireOnboarding=true, hasCompletedOnboarding=false, current path:', currentPath);
      }
    } else if (!requireOnboarding && hasCompletedOnboarding) {
      // Only redirect away from onboarding if we're actually on the onboarding page
      if (isOnOnboardingPage) {
        shouldRedirect = true;
        targetPath = '/dashboard';
        redirectScenario = 'from_onboarding';
        console.log('[ProtectedRoute] Will redirect from onboarding to dashboard - already completed, current path:', currentPath);
      }
    }

    // Use localStorage-based redirect tracking by scenario (not by current path)
    // This prevents loops because the scenario key doesn't change when we navigate
    const redirectStorageKey = shouldRedirect && redirectScenario
      ? `bodai_redirect_${user.id}_${redirectScenario}`
      : null;
    const hasRedirected = redirectStorageKey ? localStorage.getItem(redirectStorageKey) === 'true' : false;

    // Add detailed logging to understand what's happening
    console.log('[ProtectedRoute] Redirect check:', {
      requireOnboarding,
      hasCompletedOnboarding,
      isOnOnboardingPage,
      currentPath,
      userLoaded,
      checkingOnboarding,
      hasRedirected,
      redirectScenario,
      hasCheckedOnboarding: hasCheckedOnboardingRef.current
    });

    // Perform redirect only once per scenario using shared localStorage tracking
    if (shouldRedirect && targetPath && redirectStorageKey && !hasRedirected) {
      localStorage.setItem(redirectStorageKey, 'true');
      navigate(targetPath, { replace: true });
    }
  }, [userLoaded, checkingOnboarding, user, requireOnboarding, hasCompletedOnboarding, location.pathname, navigate]);

  if (!userLoaded || checkingOnboarding) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '14px' }}>{t('common.buttons.loading')}</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect
  }

  if (requireOnboarding && !hasCompletedOnboarding) {
    // Show loading while redirecting
    if (location.pathname !== '/onboarding') {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#666', fontSize: '14px' }}>Redirecting...</p>
        </div>
      );
    }
  }

  // Only show redirecting message if we're actually on the onboarding page
  if (!requireOnboarding && hasCompletedOnboarding && location.pathname === '/onboarding') {
    // Show loading while redirecting
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '14px' }}>Redirecting...</p>
      </div>
    );
  }

  // IMPORTANT: Do NOT redirect if we're on other pages - just render the children
  // This allows users to stay on /practice, /grades, /analyses, etc. when refreshing

  return children;
}


// Homepage Route Component - handles conditional rendering
// Note: Authenticated users can visit the homepage - Clerk handles redirects from sign-in/sign-up pages
function HomepageRoute({ user, isLoaded, navigate, onNewAnalysis }) {
  const location = useLocation();
  const isSignedIn = !!user;

  // Prevent unwanted redirects when on homepage
  useEffect(() => {
    if (location.pathname === '/' && isLoaded) {
      console.log('[HomepageRoute] Mounted on homepage, user signed in:', !!user);

      // Clear any potential redirect URLs from Clerk in URL params
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('__clerk_redirect_url') || urlParams.has('redirect_url')) {
        console.log('[HomepageRoute] Found redirect URL in params, clearing it');
        urlParams.delete('__clerk_redirect_url');
        urlParams.delete('redirect_url');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
      }

      // Clear any Clerk redirect state from sessionStorage
      try {
        Object.keys(sessionStorage).forEach(key => {
          if (key.includes('redirect') || key.includes('clerk_redirect')) {
            console.log('[HomepageRoute] Found redirect key in sessionStorage:', key, '- clearing it');
            sessionStorage.removeItem(key);
          }
        });
      } catch (e) {
        // Ignore errors
      }

      // Monitor for any navigation away from homepage
      const currentPath = window.location.pathname;
      if (currentPath !== '/') {
        console.warn('[HomepageRoute] WARNING: Current path is not "/" but', currentPath, '- this should not happen!');
      }
    }
  }, [location.pathname, isLoaded, user]);

  // Scroll to anchored sections (e.g. #product, #how-it-works) when hash changes on homepage
  useEffect(() => {
    if (location.pathname !== '/' || !location.hash) return;
    const id = location.hash.replace('#', '');
    const target = document.getElementById(id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.pathname, location.hash]);

  // Generate structured data for homepage
  const structuredData = generateSoftwareApplicationSchema({
    name: 'BodAI',
    description: SEOConfigs.homepage.description,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock'
    }
  });

  return (
    <>
      <SEO
        {...SEOConfigs.homepage}
        structuredData={structuredData}
      />
      <Header />
      <Hero isSignedIn={isSignedIn} onNewAnalysis={onNewAnalysis} />
      <div id="try">
        <FeaturesSection />
        <HowItWorksSection />
        <SocialProofSection />
        <CTASection isSignedIn={isSignedIn} />
      </div>
      <Footer />
    </>
  );
}

export default function AppRouter() {
  const { user, isLoaded: userLoaded } = useUser();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState('uploading'); // 'uploading' | 'analyzing'
  const [result, setResult] = useState(null);
  const [userContext, setUserContext] = useState(null);
  const [viewingAnalysis, setViewingAnalysis] = useState(null);
  const [dashboardRefreshTrigger, setDashboardRefreshTrigger] = useState(0);
  const [currentAnalysisId, setCurrentAnalysisId] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [showVideoTooShortModal, setShowVideoTooShortModal] = useState(false);
  const [videoTooShortMessage, setVideoTooShortMessage] = useState('');
  const [showVideoTooLargeModal, setShowVideoTooLargeModal] = useState(false);
  const [videoTooLargeMessage, setVideoTooLargeMessage] = useState('');
  const [showVideoDuplicateModal, setShowVideoDuplicateModal] = useState(false);
  const [videoDuplicateMessage, setVideoDuplicateMessage] = useState('');
  const [journeys, setJourneys] = useState([]);
  const [journeysLoading, setJourneysLoading] = useState(true);
  const [activeJourneyId, setActiveJourneyId] = useState(null);
  const [practiceCompletionNotices, setPracticeCompletionNotices] = useState([]);
  const [hasCompletedAnalysis, setHasCompletedAnalysis] = useState(() => {
    if (user?.id) {
      return localStorage.getItem(`bodai_has_completed_analysis_${user.id}`) === 'true';
    }
    return localStorage.getItem('bodai_has_completed_analysis') === 'true';
  });
  
  // Track onboarding completion status for language preference loading
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(() => {
    if (user?.id) {
      return localStorage.getItem(`bodai_questions_completed_${user.id}`) === 'true';
    }
    return false;
  });

  // Error state for ErrorDisplay component
  const [errorInfo, setErrorInfo] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0); // Real upload progress (0-100)
  const apiBase = import.meta.env.DEV
    ? '' // use Vite proxy in dev
    : (import.meta.env.VITE_API_URL || ''); // Use relative URLs in production if VITE_API_URL not set

  // Sync hasCompletedAnalysis state when user changes
  // ALWAYS check API to get the real status, not just localStorage
  useEffect(() => {
    if (!user?.id) return;

    const controller = new AbortController();
    const checkAnalyses = async () => {
      try {
        const res = await fetch(`${apiBase}/api/analyses?limit=1&all=true`, {
          headers: {
            'X-Clerk-User-Id': user.id,
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        });
        if (res.ok) {
          const data = await res.json();
          const hasAnalyses = data?.analyses?.length > 0;

          // Always update state and localStorage based on API response (source of truth)
          if (hasAnalyses) {
            localStorage.setItem(`bodai_has_completed_analysis_${user.id}`, 'true');
            setHasCompletedAnalysis(true);
          } else {
            // Only clear localStorage if API confirms no analyses
            localStorage.removeItem(`bodai_has_completed_analysis_${user.id}`);
            setHasCompletedAnalysis(false);
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to check analyses:', err);
          // On error, fall back to localStorage
          const saved = localStorage.getItem(`bodai_has_completed_analysis_${user.id}`);
          setHasCompletedAnalysis(saved === 'true');
        }
      }
    };

    checkAnalyses();
    return () => controller.abort();
  }, [user?.id, apiBase]);

  // Version check - detect app updates and prompt refresh
  useEffect(() => {
    if (import.meta.env.PROD) {
      const handleUpdateAvailable = (updateInfo) => {
        console.log('[AppRouter] Update available:', updateInfo);

        // Show notification to user
        const shouldRefresh = window.confirm(
          i18n.language === 'he'
            ? 'עדכון חדש זמין! האם לרענן את הדף כדי לקבל את הגרסה החדשה?'
            : 'A new update is available! Refresh the page to get the latest version?'
        );

        if (shouldRefresh) {
          // Clear caches before refresh
          if ('caches' in window) {
            caches.keys().then(names => {
              names.forEach(name => caches.delete(name));
            });
          }
          // Clear React state caches
          sessionStorage.clear();
          // Reload page
          window.location.reload(true);
        }
      };

      // Check for updates every 2 minutes
      const cleanup = startVersionCheck(handleUpdateAvailable, 120000);

      return cleanup;
    }
  }, [i18n.language]);

  // Note: Clerk handles redirects via afterSignInUrl and afterSignUpUrl
  // We don't need custom redirect logic here - authenticated users can visit the homepage

  // Load user language preference from database when user loads
  // This ensures Israeli users see Hebrew and non-Israeli users see English
  // CRITICAL: Only load from database if user has completed onboarding
  // New users should keep their location-based detected language until they complete onboarding
  useEffect(() => {
    // Check if user has completed onboarding (use state value)
    const userCompletedOnboarding = hasCompletedOnboarding;
    
    if (user?.id && i18n.isInitialized && userCompletedOnboarding) {
      const loadUserLanguagePreference = async () => {
        try {
          const res = await fetch(`${apiBase}/api/user/language-preference`, {
            headers: {
              'X-Clerk-User-Id': user.id,
              'Content-Type': 'application/json'
            }
          });
          
          if (res.ok) {
            const data = await res.json();
            const dbLanguage = data.languagePreference;
            
            // Only change language if database has a preference (not null) AND it differs from current
            // If dbLanguage is null, user hasn't completed onboarding yet - keep location-based language
            if (dbLanguage && ['en', 'he'].includes(dbLanguage) && dbLanguage !== i18n.language) {
              console.log(`[AppRouter] Loading user language preference from database: ${dbLanguage}`);
              await i18n.changeLanguage(dbLanguage);
              // Update localStorage to match database
              localStorage.setItem('i18nextLng', dbLanguage);
            } else if (!dbLanguage) {
              // No preference in database yet (new user) - keep current location-based language
              console.log(`[AppRouter] No language preference in database yet - keeping location-based: ${i18n.language}`);
            }
          }
        } catch (err) {
          console.warn('[AppRouter] Failed to load language preference from database:', err);
          // If database fetch fails, keep current language (which was detected by location)
        }
      };
      
      loadUserLanguagePreference();
    } else if (user?.id && i18n.isInitialized && !userCompletedOnboarding) {
      // For new users who haven't completed onboarding yet:
      // Keep the language that was detected by location-based detection
      // Don't override it with database (which might not have the preference yet)
      const currentLang = i18n.language || 'en';
      console.log(`[AppRouter] New user - keeping location-based detected language: ${currentLang}`);
      // Ensure it's saved to localStorage so it persists
      if (currentLang && ['en', 'he'].includes(currentLang)) {
        localStorage.setItem('i18nextLng', currentLang);
      }
    }
  }, [user?.id, i18n, apiBase, hasCompletedOnboarding]);

  // Load user context from localStorage on mount
  useEffect(() => {
    if (user?.id) {
      const savedContext = localStorage.getItem(`bodai_user_context_${user.id}`);
      if (savedContext) {
        try {
          setUserContext(JSON.parse(savedContext));
        } catch (e) {
          console.warn('Failed to parse saved context:', e);
        }
      }
    }
  }, [user?.id]);

  const syncUserContextFromJourney = useCallback((journey) => {
    if (!journey || !user?.id) return;

    // Preserve existing environment feedback preference
    // Try state first, then localStorage, then default to true
    let feedbackPref = true;
    if (userContext?.includeEnvironmentFeedback !== undefined) {
      feedbackPref = userContext.includeEnvironmentFeedback;
    } else {
      const saved = localStorage.getItem(`bodai_user_context_${user.id}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.includeEnvironmentFeedback !== undefined) {
            feedbackPref = parsed.includeEnvironmentFeedback;
          }
        } catch (e) { }
      }
    }

    // Get goalSpecificContext: prefer journey.goal_context, then existing userContext, then localStorage
    let goalSpecificContext = null;
    if (journey.goal_context && Object.keys(journey.goal_context).length > 0) {
      goalSpecificContext = journey.goal_context;
    } else if (userContext?.goalSpecificContext && Object.keys(userContext.goalSpecificContext).length > 0) {
      goalSpecificContext = userContext.goalSpecificContext;
    } else {
      const saved = localStorage.getItem(`bodai_user_context_${user.id}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.goalSpecificContext && Object.keys(parsed.goalSpecificContext).length > 0) {
            goalSpecificContext = parsed.goalSpecificContext;
          }
        } catch (e) { }
      }
    }

    const context = {
      primaryGoal: journey.focus_slug || 'general',
      confidenceLevel: journey.confidence_level || 'medium',
      language: i18n.language || 'en',
      includeEnvironmentFeedback: feedbackPref
    };
    if (goalSpecificContext) {
      context.goalSpecificContext = goalSpecificContext;
    }

    setUserContext(context);
    localStorage.setItem(`bodai_user_context_${user.id}`, JSON.stringify(context));
  }, [i18n.language, user?.id, userContext]);

  const fetchJourneysRef = useRef(null);
  const fetchJourneys = useCallback(async () => {
    if (!user?.id) {
      setJourneys([]);
      setActiveJourneyId(null);
      setJourneysLoading(false);
      return null;
    }

    setJourneysLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/journeys`, {
        headers: {
          'X-Clerk-User-Id': user.id,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        const data = await res.json();
        const journeyList = Array.isArray(data.journeys) ? data.journeys : [];
        setJourneys(journeyList);

        // Check localStorage or default (don't check URL here to avoid loops)
        const storedActive = localStorage.getItem(`bodai_active_journey_${user.id}`);
        let nextActive = storedActive && journeyList.some(j => j.id === storedActive) ? storedActive : null;
        if (!nextActive && journeyList.length > 0) {
          nextActive = (journeyList.find(j => j.is_default) || journeyList[0]).id;
        }

        if (nextActive) {
          localStorage.setItem(`bodai_active_journey_${user.id}`, nextActive);
          const activeJourney = journeyList.find(j => j.id === nextActive);
          if (activeJourney) {
            syncUserContextFromJourney(activeJourney);
          }
        }
        setActiveJourneyId(nextActive);

        return { nextActive, journeyList };
      }
      return null;
    } catch (err) {
      console.error('Failed to fetch journeys:', err);
      return null;
    } finally {
      setJourneysLoading(false);
    }
  }, [apiBase, user?.id, syncUserContextFromJourney, activeJourneyId]);

  // Store the function in ref so it can be called from other places
  fetchJourneysRef.current = fetchJourneys;

  // Track navigation in Sentry breadcrumbs and PostHog
  // This must be after activeJourneyId is defined
  useEffect(() => {
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: `Navigated to ${location.pathname}${location.search}`,
        level: 'info',
        data: {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
      });
    }

    // Track page view in PostHog with context
    const pageName = location.pathname === '/' ? 'homepage' : location.pathname.replace(/^\//, '').replace(/\//g, '_') || 'homepage';
    trackPageView(pageName, {
      is_authenticated: !!user,
      has_completed_onboarding: user ? (localStorage.getItem(`bodai_questions_completed_${user.id}`) === 'true') : false,
      active_journey_id: activeJourneyId,
      query_params: location.search,
    });
  }, [location.pathname, location.search, location.hash, user, activeJourneyId]);

  // Fetch journeys only when user changes, not on every render
  const lastUserIdRef = useRef(null);
  useEffect(() => {
    if (user?.id && user.id !== lastUserIdRef.current) {
      lastUserIdRef.current = user.id;
      fetchJourneys();
    } else if (!user?.id) {
      lastUserIdRef.current = null;
    }
  }, [user?.id]); // Only depend on user.id, not fetchJourneys

  // Track if we've already redirected based on journeys to prevent loops
  const hasRedirectedForJourneysRef = useRef(false);

  useEffect(() => {
    // Only redirect to onboarding if:
    // 1. Journeys are fully loaded (not loading)
    // 2. User is signed in
    // 3. There are no journeys
    // 4. We're NOT already on onboarding or auth pages
    // 5. We haven't already redirected for this scenario
    if (journeysLoading || !user?.id) {
      hasRedirectedForJourneysRef.current = false;
      return;
    }

    // If journeys exist, reset redirect tracking
    if (journeys.length > 0) {
      hasRedirectedForJourneysRef.current = false;
      return;
    }

    // Check if user has already completed onboarding questions (as a hint to avoid redirect loop)
    const hasFinishedQuestions = localStorage.getItem(`bodai_questions_completed_${user.id}`) === 'true';
    if (hasFinishedQuestions) {
      console.log('[AppRouter] Skipping onboarding redirect - questions already completed even if journeys list is empty');
      hasRedirectedForJourneysRef.current = false;
      return;
    }

    const isOnOnboarding = location.pathname === '/onboarding';
    const isAuthPage = location.pathname.startsWith('/sign-in') || location.pathname.startsWith('/sign-up');
    const isPricingPage = location.pathname === '/pricing';
    const isHomepage = location.pathname === '/';
    const protectedRoutes = ['/dashboard', '/practice', '/grades', '/analyses', '/my-progress', '/settings', '/subscription', '/new-analysis'];
    const isOnProtectedRoute = protectedRoutes.some(route => location.pathname.startsWith(route));
    const isAnalysisRoute = location.pathname.startsWith('/analysis/');

    // Only redirect if we're on a protected route and not already on onboarding, and haven't already redirected
    if (!isOnOnboarding && !isAuthPage && !isPricingPage && (isHomepage || isOnProtectedRoute || isAnalysisRoute) && !hasRedirectedForJourneysRef.current) {
      console.log('[AppRouter] Redirecting to onboarding - no journeys found, current path:', location.pathname);
      hasRedirectedForJourneysRef.current = true;
      navigate('/onboarding', { replace: true });
    }
  }, [journeysLoading, journeys.length, user?.id, navigate, location.pathname]);

  // This useEffect is redundant - the one above (lines 680-720) already checks API on every user change
  // Removing duplicate logic to avoid race conditions

  // Sync journeyId with URL query parameter (only when URL changes, not when state changes)
  const lastUrlJourneyIdRef = useRef(null);
  useEffect(() => {
    if (!user?.id || journeysLoading) return;

    const searchParams = new URLSearchParams(location.search);
    const urlJourneyId = searchParams.get('journey');

    // Only process if URL journey ID actually changed
    if (urlJourneyId === lastUrlJourneyIdRef.current) {
      return;
    }
    lastUrlJourneyIdRef.current = urlJourneyId;

    // If URL has a journey param and it's different from state, update state
    if (urlJourneyId && urlJourneyId !== activeJourneyId) {
      // Verify the journey exists in the journeys list
      const journeyExists = journeys.some(j => j.id === urlJourneyId);
      if (journeyExists) {
        setActiveJourneyId(urlJourneyId);
        localStorage.setItem(`bodai_active_journey_${user.id}`, urlJourneyId);
        const journey = journeys.find(j => j.id === urlJourneyId);
        if (journey) {
          syncUserContextFromJourney(journey);
        }
      }
    } else if (!urlJourneyId && activeJourneyId && journeys.length > 0) {
      // If URL doesn't have journey param but state does, update URL
      // Only update URL if we're on a page that should show journey (dashboard, my-progress, analyses)
      const journeyPages = ['/dashboard', '/my-progress', '/grades', '/analyses'];
      if (journeyPages.includes(location.pathname)) {
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set('journey', activeJourneyId);
        navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
        lastUrlJourneyIdRef.current = activeJourneyId;
      }
    }
  }, [location.search, location.pathname, journeys, activeJourneyId, syncUserContextFromJourney, navigate, user?.id, journeysLoading]);

  const handleJourneySelect = useCallback((journeyId) => {
    setActiveJourneyId(journeyId);
    if (journeyId) {
      localStorage.setItem(`bodai_active_journey_${user.id}`, journeyId);
      const journey = journeys.find(j => j.id === journeyId);
      if (journey) {
        syncUserContextFromJourney(journey);
      }

      // Update URL with journey query parameter
      const journeyPages = ['/dashboard', '/my-progress', '/grades', '/analyses'];
      if (journeyPages.includes(location.pathname)) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('journey', journeyId);
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      }
    } else if (user?.id) {
      localStorage.removeItem(`bodai_active_journey_${user.id}`);

      // Remove journey from URL
      const journeyPages = ['/dashboard', '/my-progress', '/grades', '/analyses'];
      if (journeyPages.includes(location.pathname)) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('journey');
        const newSearch = searchParams.toString();
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
      }
    }
  }, [journeys, syncUserContextFromJourney, location.pathname, location.search, navigate]);

  const handleJourneyCreate = useCallback(async (answers) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    const payload = {
      primaryGoal: answers?.primaryGoal || 'general',
      confidenceLevel: answers?.confidenceLevel || 'medium',
      goalSpecificContext: answers?.goalSpecificContext || {},
      templateId: answers?.journeyTemplateId || answers?.templateId,
      difficultyBaseline: answers?.difficultyBaseline || null,
      commitmentLevel: answers?.commitmentLevel || answers?.practiceCommitment || null,
      practiceCommitment: answers?.practiceCommitment || answers?.commitmentLevel || null,
      consentVersion: answers?.consent?.version || null,
      consentAcceptedAt: answers?.consent?.acceptedAt || null,
      setDefault: true
    };

    const res = await fetch(`${apiBase}/api/journeys`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Clerk-User-Id': user.id
      },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to create journey');
    }

    const data = await res.json();

    // Track journey creation event
    if (data.journey) {
      captureEvent('journey_created', {
        journey_id: data.journey.id,
        focus_slug: data.journey.focus_slug,
        display_name: data.journey.display_name,
        is_default: data.journey.is_default || false,
        started_from: data.journey.started_from || 'dashboard',
        primary_goal: payload.primaryGoal,
        confidence_level: payload.confidenceLevel,
      });
    }

    // Refresh journeys list using the ref function
    if (user?.id && fetchJourneysRef.current) {
      await fetchJourneysRef.current();

      // Set the new journey as active after refresh
      if (data.journey?.id) {
        setActiveJourneyId(data.journey.id);
        localStorage.setItem(`bodai_active_journey_${user.id}`, data.journey.id);
      }
    }

    // Update URL with the new journey ID if we're on a journey page
    if (data.journey?.id) {
      const journeyPages = ['/dashboard', '/my-progress', '/grades', '/analyses'];
      if (journeyPages.includes(location.pathname)) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('journey', data.journey.id);
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      }
    }

    return data.journey;
  }, [apiBase, user?.id, syncUserContextFromJourney, location.pathname, location.search, navigate]);

  const handleJourneyDelete = useCallback(async (journeyId) => {
    if (!user?.id) return;

    try {
      const res = await fetch(`${apiBase}/api/journeys/${journeyId}`, {
        method: 'DELETE',
        headers: {
          'X-Clerk-User-Id': user.id
        }
      });

      if (!res.ok) {
        throw new Error('Failed to delete journey');
      }

      const wasActiveJourney = activeJourneyId === journeyId;

      // Refresh journeys - this will update journeys list and set new activeJourneyId
      // fetchJourneys now returns the new active journey ID so we can use it immediately
      const fetchResult = fetchJourneysRef.current ? await fetchJourneysRef.current() : null;
      const newActiveJourneyId = fetchResult?.nextActive || null;
      const updatedJourneys = fetchResult?.journeyList || [];

      // If deleted active journey, update URL to match the new active journey
      if (wasActiveJourney) {
        const journeyPages = ['/dashboard', '/my-progress', '/grades', '/analyses'];
        if (journeyPages.includes(location.pathname)) {
          if (newActiveJourneyId) {
            // Update URL with new active journey
            const searchParams = new URLSearchParams(location.search);
            searchParams.set('journey', newActiveJourneyId);
            navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
          } else if (updatedJourneys.length === 0) {
            // No journeys left, remove from URL
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete('journey');
            navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
          }
        }
      }

    } catch (err) {
      console.error('Error deleting journey:', err);
    }
  }, [apiBase, user?.id, activeJourneyId, location.pathname, location.search, navigate]);

  const handleQuestionsComplete = async (answers) => {
    try {
      // Ensure includeEnvironmentFeedback has a default value
      // Default to false (skills only) if not explicitly set
      const contextWithDefaults = {
        ...answers,
        includeEnvironmentFeedback: answers.includeEnvironmentFeedback !== undefined ? answers.includeEnvironmentFeedback : false
      };
      setUserContext(contextWithDefaults);
      localStorage.setItem(`bodai_questions_completed_${user.id}`, 'true');
      localStorage.setItem(`bodai_user_context_${user.id}`, JSON.stringify(contextWithDefaults));

      // Clear redirect tracking when onboarding is completed to allow redirect to dashboard
      if (user?.id) {
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(`bodai_redirect_${user.id}_`)) {
            localStorage.removeItem(key);
          }
        });
      }

      // Reset redirect tracking since onboarding is now complete
      hasRedirectedForJourneysRef.current = false;

      if (user?.id) {
        try {
          // Build headers with Clerk profile data for syncing
          const headers = {
            'Content-Type': 'application/json',
            'X-Clerk-User-Id': user.id
          };

          // Add user profile data to headers for syncing with Supabase (sanitize values to ensure valid HTTP header characters)
          const email = user.primaryEmailAddress?.emailAddress || (user.emailAddresses && user.emailAddresses[0]?.emailAddress);
          if (email) {
            headers['X-User-Email'] = sanitizeHeaderValue(email);
          }
          if (user.firstName) {
            headers['X-User-First-Name'] = sanitizeHeaderValue(user.firstName);
          }
          if (user.lastName) {
            headers['X-User-Last-Name'] = sanitizeHeaderValue(user.lastName);
          }
          const phone = user.primaryPhoneNumber?.phoneNumber || (user.phoneNumbers && user.phoneNumbers[0]?.phoneNumber);
          if (phone) {
            headers['X-User-Phone'] = sanitizeHeaderValue(phone);
          }
          if (user.imageUrl) {
            headers['X-User-Image-Url'] = sanitizeHeaderValue(user.imageUrl);
          }

          const apiBase = import.meta.env.DEV
            ? '' // use Vite proxy in dev
            : (import.meta.env.VITE_API_URL || ''); // Use relative URLs in production if VITE_API_URL not set

          // Include current language in onboarding data so it's saved to the database
          const answersWithLanguage = {
            ...answers,
            language: i18n.language || 'en'
          };
          
          const onboardingRes = await fetch(`${apiBase}/api/user/onboarding`, {
            method: 'POST',
            headers,
            body: JSON.stringify(answersWithLanguage)
          });

          if (onboardingRes.ok) {
            // Track onboarding completion event
            captureEvent('onboarding_completed', {
              primary_goal: answers?.primaryGoal,
              confidence_level: answers?.confidenceLevel,
              practice_commitment: answers?.practiceCommitment || answers?.commitmentLevel,
            });
          }
        } catch (err) {
          console.error('Failed to save onboarding to database:', err);
          // Don't throw - we still want to navigate even if API call fails
          // The localStorage is already set, so the user can continue
        }
      }

      // Wait a bit to ensure the database has been updated before navigating
      // This prevents race condition where ProtectedRoute checks onboarding before it's saved
      await new Promise(resolve => setTimeout(resolve, 500));

      // Force a refresh of the onboarding check by clearing the check ref
      // This will make ProtectedRoute re-check the onboarding status
      // Note: We can't directly update ProtectedRoute state, but clearing localStorage
      // and waiting will help, and the navigate will trigger a re-check

      // After saving onboarding, we need to ensure ProtectedRoute sees the updated status
      // The issue is that ProtectedRoute checks onboarding via API, and there's a race condition
      // Solution: After saving, wait a moment and then force a re-check by navigating
      // But first, let's make sure the API call completed successfully

      // Small delay to ensure database write is complete
      await new Promise(resolve => setTimeout(resolve, 300));

      // Use replace: true to prevent back button from going back to onboarding
      navigate('/dashboard', { replace: true });

      // Refresh journeys after onboarding
      if (user?.id && fetchJourneysRef.current) {
        fetchJourneysRef.current();
      }
    } catch (error) {
      console.error('Error in handleQuestionsComplete:', error);
      // Re-throw so the component can handle it
      throw error;
    }
  };

  const onSelect = (f) => {
    setFile(f);
    setResult('');
  };

  const onRemove = () => {
    setFile(null);
    setResult('');
  };

  const onAnalyze = async (options = {}) => {
    const correlationId = typeof crypto?.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).substring(2) + Date.now().toString(36);
    console.log('[onAnalyze] Starting client-side upload with correlationId:', correlationId, 'options:', options);

    if (!file) {
      console.log('[onAnalyze] No file selected, aborting');
      return;
    }

    // Warn user about large files (200MB+) before starting upload
    const fileSizeMB = file.size / (1024 * 1024);
    const LARGE_FILE_THRESHOLD_MB = 200;
    if (fileSizeMB >= LARGE_FILE_THRESHOLD_MB) {
      // Determine estimated upload time based on file size
      let estimatedUploadTime;
      if (fileSizeMB > 300) {
        estimatedUploadTime = t('uploadVideo.estimatedTimeVeryLarge', { defaultValue: '3-6 minutes' });
      } else if (fileSizeMB > 150) {
        estimatedUploadTime = t('uploadVideo.estimatedTimeLarge', { defaultValue: '2-4 minutes' });
      } else {
        estimatedUploadTime = t('uploadVideo.estimatedTimeMedium', { defaultValue: '1-2 minutes' });
      }
      
      const warningMessage = t('uploadVideo.largeFileWarning', {
        sizeMB: fileSizeMB.toFixed(1),
        estimatedTime: estimatedUploadTime,
        defaultValue: `This video is large (${fileSizeMB.toFixed(1)}MB). Upload may take ${estimatedUploadTime}. Continue?`
      });
      
      const shouldContinue = window.confirm(warningMessage);
      if (!shouldContinue) {
        console.log('[onAnalyze] User cancelled large file upload');
        return;
      }
    }

    // Track video upload event
    const videoDurationSeconds = file?.bodaiMeta?.durationSeconds;
    const fileSizeMBForTracking = file?.size ? (file.size / (1024 * 1024)).toFixed(2) : null;
    captureEvent('video_uploaded', {
      video_duration_seconds: videoDurationSeconds,
      file_size_mb: fileSizeMBForTracking,
      upload_method: 'client_side',
      journey_id: activeJourneyId,
      correlation_id: correlationId,
    });

    setIsLoading(true);
    setLoadingPhase('uploading'); // Start in upload phase - user must stay!
    setResult('');
    setErrorInfo(null);
    setUploadProgress(0); // Reset progress

    try {
      const isDev = import.meta.env.DEV;
      const apiUrl = isDev ? '' : (import.meta.env.VITE_API_URL || '');

      // ============================================
      // WORKFLOW: Direct Upload to S3 (no compression)
      // ============================================

      // Upload video directly to S3
      console.log('[onAnalyze] Uploading video to S3...');
      setResult(t('analysisPage.uploadingVideo', { defaultValue: 'Uploading video...' }));

      const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
      console.log(`[onAnalyze] Uploading file: ${fileSizeMB}MB`);

      let s3Key;
      try {
        // Reset progress at start
        setUploadProgress(0);
        const uploadResult = await uploadVideoToS3(
          file, // Use original file directly
          file.name,
          user.id,
          (progress) => {
            const { stage, percent } = progress;
            if (stage === 'uploading') {
              // Update real progress for LoadingView
              setUploadProgress(percent);
              setResult(t('analysisPage.uploadingVideoProgress', {
                defaultValue: `Uploading video... ${percent}%`,
                percent
              }));
            }
          }
        );
        s3Key = uploadResult.s3Key;
        console.log('[onAnalyze] S3 upload success:', s3Key);
        // Set progress to 100% on completion
        setUploadProgress(100);
      } catch (uploadErr) {
        console.error('[onAnalyze] S3 upload failed:', uploadErr);
        // Reset progress on error
        setUploadProgress(0);
        // Provide more detailed error message
        let errorMessage = 'Failed to upload video. Please check your connection and try again.';
        if (uploadErr.message?.includes('timeout')) {
          errorMessage = uploadErr.message;
        } else if (uploadErr.message?.includes('Network error')) {
          errorMessage = 'Network error during upload. Please check your internet connection and try again.';
        }
        throw new Error(errorMessage);
      }

      // Upload complete! Now user can leave - analysis runs on server
      setLoadingPhase('analyzing');
      console.log('[onAnalyze] Upload complete. Switched to analyzing phase - user can now leave.');

      // Register Analysis on Backend
      console.log('[onAnalyze] Registering analysis on backend...');
      setResult(t('analysisPage.processingVideo', {
        defaultValue: 'Video uploaded successfully. Starting analysis...'
      }));

      const analysisPayload = {
        s3_key: s3Key,
        original_filename: file.name,
        bytes: file.size,
        video_duration_seconds: file?.bodaiMeta?.durationSeconds,
        video_width: file?.bodaiMeta?.width,
        video_height: file?.bodaiMeta?.height,
        journey_id: activeJourneyId,
        userContext: { ...userContext, language: i18n.language },
        recording_question_id: options.recordingQuestionId,
        analyze_with_question: options.analyzeWithQuestion,
        // Map prompt details
        ...(options.recordingPrompt ? {
          recording_prompt_id: options.recordingPrompt.id,
          recording_prompt_title: options.recordingPrompt.title,
          recording_prompt_description: options.recordingPrompt.description,
          recording_prompt_action_item_id: options.recordingPrompt.actionItemId,
          recording_prompt_target_metric: options.recordingPrompt.targetMetric,
          recording_prompt_source: options.recordingPrompt.source,
          recording_prompt_setup: options.recordingPrompt.setup,
          recording_prompt_notice: options.recordingPrompt.whatToNotice,
          recording_prompt_tip: options.recordingPrompt.recordingTip,
          recording_prompt_difficulty: options.recordingPrompt.difficulty,
          recording_prompt_time: options.recordingPrompt.estimatedTime,
          recording_prompt_version: options.recordingPrompt.version
        } : {})
      };

      const res = await fetch(`${apiUrl}/api/analyze-video-s3`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Clerk-User-Id': user.id,
          'X-Correlation-ID': correlationId
        },
        body: JSON.stringify(analysisPayload)
      });

      // Extract correlation ID from response header if available
      const responseCorrelationId = res.headers.get('X-Correlation-ID') || correlationId;

      if (!res.ok) {
        let errorData = {};
        try {
          const text = await res.text();
          errorData = text ? JSON.parse(text) : {};
        } catch (e) {
          // If response is not JSON, use status text
          errorData = { error: `Server error: ${res.status} ${res.statusText}` };
        }

        // Report error to Sentry - wrap in try-catch to prevent Sentry errors from breaking the flow
        if (import.meta.env.VITE_SENTRY_DSN) {
          try {
            Sentry.captureException(new Error(errorData.error || `Request failed: ${res.status}`), {
              tags: {
                errorCategory: errorData.errorCategory || 'UNKNOWN_ERROR',
                correlationId: responseCorrelationId,
                endpoint: '/api/analyze-video',
                statusCode: res.status
              },
              extra: {
                errorData,
                correlationId: responseCorrelationId,
                userId: user?.id
              }
            });
          } catch (sentryError) {
            // Don't let Sentry errors break the flow - just log them
            console.warn('[Sentry] Failed to report error to Sentry:', sentryError);
          }
        }

        // Track API error in PostHog
        captureEvent('api_error', {
          endpoint: '/api/analyze-video',
          status: res.status,
          error_message: errorData.error || `Request failed: ${res.status}`,
          error_category: errorData.errorCategory || 'UNKNOWN_ERROR',
          correlation_id: responseCorrelationId,
          reason: errorData.reason,
          requires_upgrade: errorData.requiresUpgrade || false,
        });

        if (errorData.requiresUpgrade || errorData.reason === 'video_duration_exceeded') {
          setUpgradeMessage(errorData.error || 'You have reached your analysis limit. Upgrade to continue.');
          setShowUpgradeModal(true);
          setResult(null);
          setErrorInfo(null);
          return;
        }
        if (errorData.reason === 'video_too_short') {
          setVideoTooShortMessage(errorData.error || '');
          setShowVideoTooShortModal(true);
          setResult(null);
          setErrorInfo(null);
          return;
        }
        if (errorData.reason === 'video_too_large') {
          setVideoTooLargeMessage(errorData.error || 'Video is too large. Please keep uploads under 500MB.');
          setShowVideoTooLargeModal(true);
          setResult(null);
          setErrorInfo(null);
          return;
        }
        if (errorData.reason === 'duplicate_video') {
          setVideoDuplicateMessage(errorData.error || '');
          setShowVideoDuplicateModal(true);
          setResult(null);
          setErrorInfo(null);
          return;
        }

        // Set error info for ErrorDisplay component
        setErrorInfo({
          error: errorData.error || `Request failed with status ${res.status}: ${res.statusText}`,
          errorCategory: errorData.errorCategory || 'UNKNOWN_ERROR',
          correlationId: errorData.correlationId || responseCorrelationId,
          retryable: errorData.retryable !== false, // Default to true unless explicitly false
          fileName: file?.name,
          uploadedOn: new Date().toISOString()
        });
        setResult('');
        return;
      }

      // Parse JSON response with error handling
      // IMPORTANT: Store response data in a local variable BEFORE setting state
      // This allows us to check if analysis succeeded even if Sentry error occurs
      // before React state updates (which are async)
      let responseData = null;
      let data;
      try {
        const responseText = await res.text();
        if (!responseText || responseText.trim().length === 0) {
          throw new Error('Empty response from server');
        }
        data = JSON.parse(responseText);
      } catch (parseError) {
        // If JSON parsing fails, this is a real error - not a Sentry issue
        console.error('[onAnalyze] Failed to parse response JSON:', parseError);
        throw new Error(`Failed to parse server response: ${parseError.message || 'Invalid JSON format'}`);
      }

      // Validate response data
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format from server');
      }

      // Store response data BEFORE setting state (for error handling)
      responseData = {
        result: data.result || '',
        analysisId: data.analysisId,
        completedPracticePrompts: data.completedPracticePrompts || []
      };

      // SUCCESS! Set state first before any async tracking operations
      setResult(responseData.result);
      setCurrentAnalysisId(responseData.analysisId);
      setErrorInfo(null); // Clear any error state on success
      setIsLoading(false); // SUCCESS! Clear loading state
      setPracticeCompletionNotices(responseData.completedPracticePrompts);

      // Track analysis generation event in a non-blocking way
      // Wrap in try-catch to prevent tracking errors from affecting user experience
      try {
        captureEvent('analysis_generated', {
          analysis_id: responseData.analysisId,
          journey_id: activeJourneyId,
          correlation_id: responseCorrelationId
        });
      } catch (trackingError) {
        // Silently ignore tracking errors - they shouldn't affect user experience
        console.warn('[onAnalyze] Failed to track analysis event:', trackingError);
      }

    } catch (err) {
      console.error('Analysis failed:', err);

      // Check if this is a "Load failed" error from Sentry/analytics scripts
      // These errors occur when external scripts fail to load, not when the analysis itself fails
      const isLoadFailedError = err.message && (
        err.message.includes('Load failed') || 
        err.message.includes('Failed to fetch') ||
        err.message.includes('NetworkError') ||
        err.message.includes('Network request failed')
      );
      
      // Check if it's specifically a Sentry SDK error (not a real application error)
      const isSentryError = err.stack && (
        err.stack.includes('sentry') ||
        err.stack.includes('bodai.io/assets/js/sentry') ||
        err.stack.includes('sentry-BnwpII3B.js')
      );
      
      // Check if it's a JSON parsing error that might be caused by Sentry interference
      const isParsingError = err.message && (
        err.message.includes('parse') ||
        err.message.includes('JSON') ||
        err.message.includes('Unexpected token') ||
        err.message.includes('Invalid JSON')
      );
      
      // IMPORTANT: Check if we have response data (analysis succeeded before error)
      // Use local variable instead of state (which may not be updated yet due to async React updates)
      const hasAnalysisResult = responseData && (responseData.result || responseData.analysisId);
      
      // If it's a load failed error (especially from Sentry), ignore it completely
      // BUT: Only ignore if we have response data (meaning analysis succeeded)
      // If we have response data, the analysis succeeded and this is just tracking noise
      if ((isLoadFailedError || isSentryError) && hasAnalysisResult) {
        console.warn('[onAnalyze] Ignoring Load failed/Sentry error - analysis already succeeded:', err.message);
        // Don't show error to user - analysis succeeded, this is just tracking noise
        // Make sure we don't overwrite the result that was already set
        setIsLoading(false);
        return;
      }
      
      // If it's a parsing error AND it's from Sentry, ignore it
      // But if it's a real parsing error (no Sentry in stack), treat it as real
      if (isParsingError && isSentryError) {
        console.warn('[onAnalyze] Ignoring Sentry-related parsing error:', err.message);
        setIsLoading(false);
        return;
      }

      // Report to Sentry - wrap in try-catch to prevent Sentry errors from breaking the flow
      if (import.meta.env.VITE_SENTRY_DSN) {
        try {
          Sentry.captureException(err, {
            tags: {
              errorCategory: 'CLIENT_ERROR',
              correlationId
            }
          });
        } catch (sentryError) {
          // Don't let Sentry errors break the flow - just log them
          console.warn('[Sentry] Failed to report error to Sentry:', sentryError);
        }
      }

      // Only set error if we don't have response data (real error)
      // If we have response data, the analysis succeeded and we already set the result
      if (!hasAnalysisResult) {
        const errorMessage = err.message || t('analysisPage.errorGeneric', { defaultValue: 'An unexpected error occurred. Please try again.' });

        setErrorInfo({
          error: errorMessage,
          errorCategory: 'CLIENT_ERROR',
          correlationId,
          retryable: true
        });
      }
      setIsLoading(false);
    }
  };

  // Retry handler for ErrorDisplay
  const handleRetryAnalysis = useCallback(() => {
    if (file) {
      setErrorInfo(null);
      onAnalyze({ file, userContext, recordingPrompt: null });
    }
  }, [file, userContext, onAnalyze]);

  const dismissPracticeNotices = useCallback(() => setPracticeCompletionNotices([]), []);

  const handleNewAnalysis = useCallback(async () => {
    // First, check if user has remaining credits before navigating
    if (user?.id) {
      try {
        const res = await fetch(`${apiBase}/api/user/subscription-status`, {
          headers: {
            'x-clerk-user-id': user.id
          }
        });
        if (res.ok) {
          const data = await res.json();
          // If no credits remaining, show upgrade modal immediately
          if (data.creditsRemaining === 0) {
            setUpgradeMessage(t('subscription.limitModal.message'));
            setShowUpgradeModal(true);
            return; // Don't navigate to new-analysis
          }
        }
      } catch (err) {
        // If check fails, proceed anyway - server will catch it later
        console.warn('[handleNewAnalysis] Failed to check subscription status:', err);
      }
    }
    
    // User has credits, proceed to new analysis
    setFile(null);
    setResult('');
    setViewingAnalysis(null);
    setCurrentAnalysisId(null);
    navigate('/new-analysis');
  }, [user, apiBase, t, navigate]);

  const handleViewAnalysis = (analysis) => {
    if (!analysis) return;
    dismissPracticeNotices();
    setFile(null);
    setResult('');
    setViewingAnalysis(analysis);
    setCurrentAnalysisId(analysis.id);
    navigate(`/analysis/${analysis.id}`);
  };

  const handleNavigate = (page) => {
    navigate(`/${page}`);
    if (page !== 'new-analysis' && !page.startsWith('analysis/')) {
      setFile(null);
      setResult('');
      setViewingAnalysis(null);
    }
  };

  return (
    <SidebarProvider>
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        message={upgradeMessage}
      />
      <VideoTooShortModal
        isOpen={showVideoTooShortModal}
        onClose={() => setShowVideoTooShortModal(false)}
        message={videoTooShortMessage}
      />
      <VideoTooLargeModal
        isOpen={showVideoTooLargeModal}
        onClose={() => setShowVideoTooLargeModal(false)}
        message={videoTooLargeMessage}
      />
      <VideoDuplicateModal
        isOpen={showVideoDuplicateModal}
        onClose={() => setShowVideoDuplicateModal(false)}
        message={videoDuplicateMessage}
      />
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <HomepageRoute
            user={user}
            isLoaded={userLoaded}
            navigate={navigate}
            onNewAnalysis={handleNewAnalysis}
          />
        } />

        {/* Auth Routes - Redirect to dashboard if already signed in */}
        {/* Use key prop to prevent re-mounting when user state changes */}
        <Route path="/sign-in/*" element={
          user ? <Navigate to="/dashboard" replace /> : (
            <Suspense fallback={<LoadingSpinner message="Loading sign in..." size="large" />}>
              <SignInPage key="sign-in-page" />
            </Suspense>
          )
        } />
        <Route path="/sign-up/*" element={
          user ? <Navigate to="/dashboard" replace /> : (
            <Suspense fallback={<LoadingSpinner message="Loading sign up..." size="large" />}>
              <SignUpPage key="sign-up-page" />
            </Suspense>
          )
        } />

        {/* Teams & Enterprise contact (public) */}
        <Route path="/contact-teams" element={<TeamsContactPage />} />

        {/* Legal Pages (public) */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/refunds" element={<RefundsPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Protected Routes */}
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <OnboardingPage onComplete={handleQuestionsComplete} />
          </ProtectedRoute>
        } />

        {/* New Analysis Route - with subscription check */}
        <Route path="/new-analysis" element={
          <ProtectedRoute requireOnboarding>
            <SubscriptionGuard 
              user={user} 
              onShowUpgradeModal={() => {
                setUpgradeMessage(t('subscription.limitModal.message'));
                setShowUpgradeModal(true);
              }}
            >
              <AnalysisPage
                file={file}
                onSelect={onSelect}
                onRemove={onRemove}
                onAnalyze={onAnalyze}
                isLoading={isLoading}
                loadingPhase={loadingPhase}
                result={result}
                errorInfo={errorInfo}
                onRetryAnalysis={handleRetryAnalysis}
                viewingAnalysis={null}
                currentAnalysisId={currentAnalysisId}
                userContext={userContext}
                activeJourneyId={activeJourneyId}
                practiceCompletionNotices={practiceCompletionNotices}
                onDismissPracticeNotices={dismissPracticeNotices}
                hasCompletedAnalysis={hasCompletedAnalysis}
                refreshTrigger={dashboardRefreshTrigger}
                uploadProgress={uploadProgress}
                onBackToDashboard={() => {
                  setViewingAnalysis(null);
                  setResult('');
                  setFile(null);
                  setCurrentAnalysisId(null);
                  navigate('/dashboard');
                  setDashboardRefreshTrigger(prev => prev + 1);
                }}
              />
            </SubscriptionGuard>
          </ProtectedRoute>
        } />

        {/* View Existing Analysis Route */}
        <Route path="/analysis/:id" element={
          <ProtectedRoute requireOnboarding>
            <AnalysisPage
              file={file}
              onSelect={onSelect}
              onRemove={onRemove}
              onAnalyze={onAnalyze}
              isLoading={isLoading}
              loadingPhase={loadingPhase}
              result={result}
              errorInfo={errorInfo}
              onRetryAnalysis={handleRetryAnalysis}
              viewingAnalysis={viewingAnalysis}
              currentAnalysisId={viewingAnalysis?.id}
              userContext={userContext}
              activeJourneyId={activeJourneyId}
              practiceCompletionNotices={practiceCompletionNotices}
              onDismissPracticeNotices={dismissPracticeNotices}
              hasCompletedAnalysis={hasCompletedAnalysis}
              refreshTrigger={dashboardRefreshTrigger}
              uploadProgress={uploadProgress}
              onBackToDashboard={() => {
                setViewingAnalysis(null);
                setResult('');
                setFile(null);
                setCurrentAnalysisId(null);
                navigate('/dashboard');
                setDashboardRefreshTrigger(prev => prev + 1);
              }}
            />
          </ProtectedRoute>
        } />

        <Route path="/dashboard" element={
          <ProtectedRoute requireOnboarding>
            <>
              <Sidebar />
              <div className="dashboardLayout">
                <Suspense fallback={<LoadingSpinner message="Loading dashboard..." size="large" />}>
                  <Dashboard
                    onNewAnalysis={handleNewAnalysis}
                    onViewAnalysis={handleViewAnalysis}
                    refreshTrigger={dashboardRefreshTrigger}
                    journeys={journeys}
                    journeysLoading={journeysLoading}
                    activeJourneyId={activeJourneyId}
                    onSelectJourney={handleJourneySelect}
                    onStartJourney={handleJourneyCreate}
                    onDeleteJourney={handleJourneyDelete}
                  />
                </Suspense>
              </div>
            </>
          </ProtectedRoute>
        } />

        <Route path="/analyses" element={
          <ProtectedRoute requireOnboarding>
            <>
              <Sidebar />
              <div className="dashboardLayout">
                <Suspense fallback={<LoadingSpinner message="Loading analyses..." size="large" />}>
                  <MyAnalysesPage
                    onViewAnalysis={handleViewAnalysis}
                    journeys={journeys}
                    journeysLoading={journeysLoading}
                    activeJourneyId={activeJourneyId}
                    onSelectJourney={handleJourneySelect}
                    onDeleteJourney={handleJourneyDelete}
                  />
                </Suspense>
              </div>
            </>
          </ProtectedRoute>
        } />

        <Route path="/grades" element={
          <ProtectedRoute requireOnboarding>
            <>
              <Sidebar />
              <div className="dashboardLayout">
                <Suspense fallback={<LoadingSpinner message="Loading progress..." size="large" />}>
                  <MyProgressPage
                    journeys={journeys}
                    journeysLoading={journeysLoading}
                    activeJourneyId={activeJourneyId}
                    onSelectJourney={handleJourneySelect}
                    onDeleteJourney={handleJourneyDelete}
                    refreshTrigger={dashboardRefreshTrigger}
                  />
                </Suspense>
              </div>
            </>
          </ProtectedRoute>
        } />

        <Route path="/practice" element={
          <ProtectedRoute requireOnboarding>
            <>
              <Sidebar />
              <div className="dashboardLayout">
                <Suspense fallback={<LoadingSpinner message="Loading practice..." size="large" />}>
                  <PracticePage
                    journeys={journeys}
                    journeysLoading={journeysLoading}
                    activeJourneyId={activeJourneyId}
                    onSelectJourney={handleJourneySelect}
                    onDeleteJourney={handleJourneyDelete}
                  />
                </Suspense>
              </div>
            </>
          </ProtectedRoute>
        } />

        <Route path="/pricing" element={
          <>
            <Header />
            <Suspense fallback={<LoadingSpinner message="Loading pricing..." size="large" />}>
              <PricingPage />
            </Suspense>
          </>
        } />

        <Route path="/subscription" element={
          <ProtectedRoute requireOnboarding>
            <>
              <Sidebar />
              <div className="dashboardLayout">
                <Suspense fallback={<LoadingSpinner message="Loading subscription..." size="large" />}>
                  <SubscriptionPage />
                </Suspense>
              </div>
            </>
          </ProtectedRoute>
        } />

        <Route path="/subscription/success" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner message="Loading..." size="large" />}>
              <SubscriptionSuccessPage />
            </Suspense>
          </ProtectedRoute>
        } />

        <Route path="/subscription/cancel" element={
          <Suspense fallback={<LoadingSpinner message="Loading..." size="large" />}>
            <SubscriptionCancelPage />
          </Suspense>
        } />

        <Route path="/courses" element={
          <ProtectedRoute requireOnboarding>
            <>
              <Sidebar />
              <div className="dashboardLayout">
                <Suspense fallback={<LoadingSpinner message="Loading courses..." size="large" />}>
                  <CoursesPage />
                </Suspense>
              </div>
            </>
          </ProtectedRoute>
        } />
        <Route path="/courses/*" element={<Navigate to="/courses" replace />} />

        <Route path="/settings" element={
          <ProtectedRoute requireOnboarding>
            <>
              <Sidebar />
              <div className="dashboardLayout">
                <Suspense fallback={<LoadingSpinner message="Loading settings..." size="large" />}>
                  <SettingsPage />
                </Suspense>
              </div>
            </>
          </ProtectedRoute>
        } />

        {/* Redirect unknown routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SidebarProvider>
  );
}

